import {
  takeEvery,
  takeLatest,
  put,
  call,
  all,
  select,
  take,
  race,
  cancel,
  fork,
  cancelled,
} from "typed-redux-saga/macro";
import { callGraphQLApi, callApi } from "../../utils/SagaUtils";
import GraphQLService from "../../services/GraphQL";

import { insertCategory, deleteCategory } from "./FindProductsSchema";

import {
  LOAD_FIND_PRODUCTS_TOP_CATEGORIES,
  LOAD_FIND_PRODUCTS_CHILD_CATEGORIES,
  FETCH_FIND_PRODUCTS_TOP_CATEGORIES,
  FETCH_FIND_PRODUCTS_CHILD_CATEGORIES,
  LoadFindProductsTopCategoriesAction,
  LoadFindProductsChildCategoriesAction,
  CLOSE_ALL_FIND_PRODUCT_SUBSCRIPTIONS,
  CloseAllFindProductsCategorySubscriptionsAction,
  RESET_FIND_PRODUCTS,
  LOAD_PRODUCTS_FOR_FIND_PRODUCTS_CATEGORY,
  FETCH_FIND_PRODUCTS_FROM_CATEGORY,
  LoadProductsFromCategoryForFindProductsCategoryAction,
  FETCH_PRODUCTS_FOR_FIND_PRODUCTS_CATEGORY,
  RESET_FIND_PRODUCTS_PRODUCTS_LIST,
  UPDATE_PRODUCTS_CATEGORY,
  CHANGE_PRODUCTS_CATEGORY,
  UpdateProductsCategoryAction,
  INSERT_PRODUCTS_TO_CATEGORY,
  DELETE_PRODUCTS_TO_CATEGORY,
  InsertSyncLogPayload,
  UpdateProductsCategoryResponse,
  FETCH_PRODUCTS_FOR_FIND_PRODUCTS,
  FETCH_FIND_PRODUCTS_COLORS,
  loadProductsForFindProductsAction,
  LoadFindProductsColorsAction,
  FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY,
  QUERY_PRODUCT_FOR_FIND_PRODUCTS,
  QUERY_PRODUCT_FOR_FIND_PRODUCTS_URL,
  QueryProductForFindProductsAction,
  GetProductListPayload,
  LOAD_FIND_PRODUCTS_PAGE_DETAILS,
  LoadFindProductsPageDetailsAction,
} from "./FindProductsTypes";
import { loadFindProductsPageDetailsComplete } from "./FindProductsActions";

import { getData, postData } from "../../services/ApiService";
import { acquireEndpoint } from "../../utils/SmartMerchandiserAPI";
import {
  selectMaxSequenceInList,
  selectProductsWithInvalidSequences,
} from "../product-list/ProductListSelectors";
import { CHANGE_PRODUCT_LIST_SEQUENCE } from "../product-list/ProductListTypes";
import {
  loadProductIdsFromFindProductsCategory,
  resetQueryProductForFindProducts,
} from "./FindProductsActions";
import {
  selectGetProductIds,
  selectProductByIdForFindProducts,
} from "./FindProductSelectors";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";
import { MODAL_CLOSED } from "store/modal/ModalTypes";
import { productLoadingCancelled } from "store/product-list/ProductListActions";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import AppEnv from "../../utils/AppEnv";
import AppState from "../AppState";

const PRODUCT_DETAILS_PAGESIZE = +(AppEnv.PRODUCT_DETAILS_PAGESIZE ?? 4);
const DISPLAY_PAGE_SIZE = +(AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24);

function findProductsChunkKeyFromPayload(payload: {
  productIds?: string[] | string;
}): string {
  const ids = payload?.productIds;
  if (Array.isArray(ids)) return ids.join(",");
  if (typeof ids === "string") return ids;
  return "";
}

function isFindProductsChunkResponse(
  action: { type: string; payload?: any },
  expected: {
    totalPagesFetched: number;
    totalChunksInBatch: number;
    chunkKey: string;
  },
): boolean {
  if (action.payload?.skipFindProductsPagination === true) return false;
  const tf = Number(action.payload?.totalPagesFetched);
  const tc = Number(action.payload?.totalChunksInBatch);
  if (tf !== expected.totalPagesFetched || tc !== expected.totalChunksInBatch) {
    return false;
  }
  return findProductsChunkKeyFromPayload(action.payload) === expected.chunkKey;
}

/** Wait until all in-flight find-products color requests have settled (SUCCESS/FAILURE). */
function* waitForFindProductsColorsIdle() {
  while (true) {
    const inflight = yield* select(
      (s: AppState) => s.findProducts?.findProductsColorsInflightCount ?? 0,
    );
    if (inflight <= 0) {
      return;
    }
    yield* take(
      (a: { type: string }) =>
        a.type === FETCH_FIND_PRODUCTS_COLORS.SUCCESS ||
        a.type === FETCH_FIND_PRODUCTS_COLORS.FAILURE,
    );
  }
}

function* putFindProductsProductDetailsInChunks(
  productIds: string[],
  base: Pick<
    GetProductListPayload,
    "catalogId" | "storeId" | "localeCode" | "categoryId"
  >,
  options?: { skipFindProductsPagination?: boolean },
) {
  const productCount = productIds.length;
  const totalChunksInBatch = Math.ceil(productCount / PRODUCT_DETAILS_PAGESIZE);
  let numberOfAPIcallsInChunks = totalChunksInBatch;
  let currentIndex = 0;
  let totalPagesFetched = 0;
  while (numberOfAPIcallsInChunks !== 0) {
    numberOfAPIcallsInChunks--;
    totalPagesFetched += 1;
    const pagesFetchedThisChunk = totalPagesFetched;
    const productIdsChunk = productIds.slice(
      currentIndex,
      currentIndex + PRODUCT_DETAILS_PAGESIZE,
    );
    const payload: GetProductListPayload = {
      ...base,
      productIds: productIdsChunk,
      totalPagesFetched: pagesFetchedThisChunk,
      totalChunksInBatch,
    };
    if (options?.skipFindProductsPagination) {
      payload.skipFindProductsPagination = true;
    }
    yield* put({
      type: FETCH_PRODUCTS_FOR_FIND_PRODUCTS.REQUEST,
      payload,
    });
    if (!options?.skipFindProductsPagination) {
      const chunkKey = productIdsChunk.join(",");
      const raceResult = yield* race({
        succeeded: take((a: { type: string; payload?: any }) =>
          a.type === FETCH_PRODUCTS_FOR_FIND_PRODUCTS.SUCCESS &&
          isFindProductsChunkResponse(a, {
            totalPagesFetched: pagesFetchedThisChunk,
            totalChunksInBatch,
            chunkKey,
          }),
        ),
        errored: take((a: { type: string; payload?: any }) =>
          a.type === FETCH_PRODUCTS_FOR_FIND_PRODUCTS.FAILURE &&
          a.payload?.skipFindProductsPagination !== true,
        ),
      });
      if (raceResult.errored) {
        break;
      }
    }
    currentIndex += PRODUCT_DETAILS_PAGESIZE;
  }
}

function* loadFindProductsTopCategories(
  action: LoadFindProductsTopCategoriesAction,
) {
  const actionType = FETCH_FIND_PRODUCTS_TOP_CATEGORIES;
  const constName = Object.keys({
    FETCH_FIND_PRODUCTS_TOP_CATEGORIES,
  })[0].toString();
  const catalogId = yield* select(selectCurrentCatalogId);
  const storeId = yield* select(selectCurrentStoreId);

  const endpoint = acquireEndpoint(constName, catalogId);
  const headersObj = {
    "x-currency-code": "USD",
    "x-store-id": storeId,
    "x-catalog-id": catalogId,
  };
  try {
    const response = yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
    yield* all(response.payload.data.CatalogToCategory);
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_FIND_PRODUCTS_TOP_CATEGORIES.FAILURE,
      message: e.message,
    });
  }
}

function* loadFindProductsChildCategories(
  action: LoadFindProductsChildCategoriesAction,
) {
  const actionType = FETCH_FIND_PRODUCTS_CHILD_CATEGORIES;
  const constName = Object.keys({
    FETCH_FIND_PRODUCTS_CHILD_CATEGORIES,
  })[0].toString();
  const categoryId = action.payload?.categoryId
    ? action.payload.categoryId
    : yield* select(selectCurrentCategory);
  const storeId = yield* select(selectCurrentStoreId);
  const catalogId = yield* select(selectCurrentCatalogId);

  const endpoint = acquireEndpoint(constName, categoryId);
  const headersObj = {
    "x-currency-code": "USD",
    "x-store-id": storeId,
    "x-catalog-id": catalogId,
  };
  try {
    const response = yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
    yield* all(response.payload.data.CategoryToCategory_aggregate.nodes);
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_FIND_PRODUCTS_CHILD_CATEGORIES.FAILURE,
      message: e.message,
    });
  }
}

function* closeAllSubscriptions(
  action: CloseAllFindProductsCategorySubscriptionsAction,
) {
  try {
    yield* put({ type: RESET_FIND_PRODUCTS });
  } catch (e: any) {
    console.error(e);
  }
}

function* loadProductsFromCategory(
  action: LoadProductsFromCategoryForFindProductsCategoryAction,
) {
  try {
    const actionType = FETCH_FIND_PRODUCTS_FROM_CATEGORY;
    const categoryId = action.payload?.categoryId
      ? action.payload.categoryId
      : yield* select(selectCurrentCategory);
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const constName = Object.keys({
      FETCH_FIND_PRODUCTS_FROM_CATEGORY,
    })[0].toString();

    const endpoint = acquireEndpoint(constName, categoryId);
    const headersObj = {
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };
    yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );

    yield* put({ type: RESET_FIND_PRODUCTS_PRODUCTS_LIST });
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_PRODUCTS_FOR_FIND_PRODUCTS_CATEGORY.FAILURE,
      message: e.message,
    });
  }
}

function* changeProductsCategory(action: UpdateProductsCategoryAction) {
  yield* put({ type: UPDATE_PRODUCTS_CATEGORY.REQUEST });
  try {
    const productsToBeAdded = action.payload.productsToBeAdded;
    const productsToBeRemoved = action.payload.productsToBeRemoved;
    const productIdsToRemove = Object.keys(productsToBeRemoved);
    const catalogId = action.payload.catalogId;
    const categoryId = action.payload.categoryId;
    let maxSequence = yield* select(selectMaxSequenceInList);
    let invalidSequenceProducts: string[] = yield* select(
      selectProductsWithInvalidSequences,
    );
    invalidSequenceProducts = invalidSequenceProducts.filter(
      (product) => !productIdsToRemove.includes(product),
    );
    if (invalidSequenceProducts.length > 0) {
      const sequencePayload = {
        catalogId,
        categoryId,
        productSequences: invalidSequenceProducts.map((product) => {
          maxSequence += 1;
          return {
            productId: product,
            sequence: maxSequence,
          };
        }),
      };
      yield* put({
        payload: sequencePayload,
        type: CHANGE_PRODUCT_LIST_SEQUENCE,
      });
    }
    // Insert
    if (Object.keys(productsToBeAdded).length > 0) {
      let prepareProductsToBeAdded: {
        catalogId: string;
        categoryId: string;
        productId: string;
        sequence: number;
      }[] = [];

      const syncLogInserts: InsertSyncLogPayload[] = [];
      Object.keys(productsToBeAdded).forEach((key) => {
        const product = productsToBeAdded[key];
        const productId = product.productId;
        prepareProductsToBeAdded.push({
          catalogId,
          categoryId,
          productId,
          sequence: maxSequence + 1,
        });
        maxSequence += 1;
        syncLogInserts.push({
          tableName: "CategoryToProduct",
          operationType: "INSERTED",
          keyName1: "catalogId",
          keyName2: "categoryId",
          keyName3: "productId",
          newKeyValue1: catalogId,
          newKeyValue2: categoryId,
          newKeyValue3: productId,
        });
      });

      const insertQuery = {
        operationName: "InsertCategoryToProduct",
        query: insertCategory,
        variables: {
          products: prepareProductsToBeAdded,
          syncLogInserts,
        },
      };
      const result: UpdateProductsCategoryResponse = yield callGraphQLApi(
        INSERT_PRODUCTS_TO_CATEGORY,
        GraphQLService.query,
        insertQuery,
      );
      /**
       * TODO : Add success and error handling
       */
      if (result && result.type === INSERT_PRODUCTS_TO_CATEGORY.SUCCESS) {
      }
    }

    //Delete
    if (Object.keys(productsToBeRemoved).length > 0) {
      let prepareProductsToBeRemoved: string[] = [];
      const syncLogInserts: InsertSyncLogPayload[] = [];
      Object.keys(productsToBeRemoved).forEach((key, index) => {
        const product = productsToBeRemoved[key];
        const productId = product.productId;
        prepareProductsToBeRemoved.push(productId);
        syncLogInserts.push({
          tableName: "CategoryToProduct",
          operationType: "DELETED",
          keyName1: "catalogId",
          keyName2: "categoryId",
          keyName3: "productId",
          oldKeyValue1: catalogId,
          oldKeyValue2: categoryId,
          oldKeyValue3: productId,
        });
      });

      const deleteQuery = {
        operationName: "DeleteCategoryToProduct",
        query: deleteCategory,
        variables: {
          productIds: prepareProductsToBeRemoved,
          catalogId,
          categoryId,
          syncLogInserts,
        },
      };
      const result = yield callGraphQLApi(
        DELETE_PRODUCTS_TO_CATEGORY,
        GraphQLService.query,
        deleteQuery,
      );
      /**
       * TODO : Add success and error handling
       */
      if (result && result.type === DELETE_PRODUCTS_TO_CATEGORY.SUCCESS) {
      }
    }
    yield* put({ type: UPDATE_PRODUCTS_CATEGORY.SUCCESS });
  } catch (e: any) {
    console.log(e);
    yield* put({ type: UPDATE_PRODUCTS_CATEGORY.FAILURE, message: e.message });
  }
}

function* loadProductListForFindProductsModal(
  action: loadProductsForFindProductsAction,
) {
  try {
    const { productIds } = action.payload;
    const localeCode = yield* select(selectCurrentLocale);
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const currentCategoryInMainSequence = yield* select(selectCurrentCategory);
    let updatedPayload = {
      ...action.payload,
      currentCategoryInMainSequence,
    };
    const actionType = FETCH_PRODUCTS_FOR_FIND_PRODUCTS;
    const constName = Object.keys({
      FETCH_PRODUCTS_FOR_FIND_PRODUCTS,
    })[0].toString();

    let commaSeperatedProductIds: string = "";

    if (productIds && Array.isArray(productIds) && productIds.length) {
      commaSeperatedProductIds = String(
        productIds.length > 1
          ? productIds.join(",").toString()
          : productIds.length && productIds[0],
      );
    }

    const endpoint = acquireEndpoint(constName, commaSeperatedProductIds);
    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    const response = yield* call(
      callApi,
      actionType,
      getData,
      updatedPayload,
      endpoint,
      headersObj,
    );

    if (response?.payload?.results && productIds?.length) {
      const colorProductIds: string[] = [];
      for (const result of response.payload.results) {
        const isProductHasColors =
          result.cachedProduct?.product?.hasColors ?? false;
        const resultProductId = result.cachedProduct?.product?.productId;
        if (
          isProductHasColors &&
          resultProductId &&
          productIds.includes(resultProductId)
        ) {
          colorProductIds.push(resultProductId);
        }
      }
      if (colorProductIds.length > 0) {
        yield* put({
          type: FETCH_FIND_PRODUCTS_COLORS.REQUEST,
          payload: {
            productIds: colorProductIds,
            storeId: storeId || "",
            localeCode,
            catalogId,
            categoryId: action.payload.categoryId || "",
            isRefreshingAllProducts: false,
          },
        });
      }
    }
  } catch (e: any) {
    yield* put({
      type: FETCH_PRODUCTS_FOR_FIND_PRODUCTS.FAILURE,
      message: e.message,
    });
  }
}

function* fetchFindProductsColorsData(action: LoadFindProductsColorsAction) {
  try {
    const { productIds, storeId, localeCode, isRefreshingAllProducts } =
      action.payload;
    if (!productIds?.length) {
      return;
    }
    const commaSeperatedProductIds = productIds.join(",");
    const actionType = FETCH_FIND_PRODUCTS_COLORS;
    const constName = Object.keys({
      FETCH_FIND_PRODUCTS_COLORS,
    })[0].toString();
    const endpoint = acquireEndpoint(
      constName,
      commaSeperatedProductIds,
      isRefreshingAllProducts ? "true" : "false",
    );
    const headersObj = {
      "x-store-id": storeId,
      "x-locale-code": localeCode || "default",
    };
    yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
  } catch (e: any) {
    yield* put({
      type: FETCH_FIND_PRODUCTS_COLORS.FAILURE,
      message: e.message,
    });
  }
}

function* getProductIdsForFindProductsFromCategory(
  action: ReturnType<typeof loadProductIdsFromFindProductsCategory>,
) {
  try {
    const actionType = FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY;
    const { categoryId, catalogId, storeId, localeCode } = action.payload;
    const constName =
      FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.REQUEST.toString();
    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    const endpoint = acquireEndpoint(constName, categoryId);
    const result = !action.payload.productIds
      ? yield* call(
          callApi,
          actionType,
          getData,
          action.payload,
          endpoint,
          headersObj,
        )
      : null;

    if ((result && result.payload) || action.payload.productIds) {
      const productIds =
        result?.payload?.productIds || action.payload.productIds;
      // Load details for the first page only; subsequent pages are loaded on demand.
      const firstPageIds = productIds.slice(0, DISPLAY_PAGE_SIZE);
      yield* putFindProductsProductDetailsInChunks(firstPageIds, {
        catalogId,
        storeId,
        localeCode,
        categoryId,
      });
      yield* waitForFindProductsColorsIdle();
    } else {
      yield* put({
        type: FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.FAILURE,
      });
    }
  } catch (e: any) {
    yield* put({
      type: FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.FAILURE,
      message: e.message,
    });
  }
}

function* queryProductForFindProducts(
  action: QueryProductForFindProductsAction,
) {
  try {
    yield* put(resetQueryProductForFindProducts());

    const actionType = QUERY_PRODUCT_FOR_FIND_PRODUCTS;
    const { search, categoryId, catalogId, storeId, localeCode, count } =
      action.payload;
    const constName = Object.keys({
      QUERY_PRODUCT_FOR_FIND_PRODUCTS_URL,
    })[0].toString();
    const endpoint = acquireEndpoint(constName);
    const headersObj = {
      "x-locale-code": localeCode,
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    let next: string | undefined = action.payload.next;
    let detailsScheduledCount = 0;

    // Fetch all pages of results
    while (true) {
      const payload: any = {};
      if (search) {
        payload.search = search;
      }
      if (categoryId) {
        payload.categoryId = categoryId;
      }
      if (count) {
        payload.count = count;
      }
      if (next) {
        payload.next = next;
      }

      const result = yield* call(
        callApi,
        actionType,
        postData,
        payload,
        endpoint,
        headersObj,
      );

      // Only process successful responses
      if (result && result.type === actionType.SUCCESS && result.payload) {
        if (!result.payload.results || result.payload.results.length === 0) {
          next = undefined;
          break;
        }

        const pageProductIds: string[] = [];
        for (const row of result.payload.results) {
          const pid = row.product?.productId;
          if (pid) {
            pageProductIds.push(pid);
          }
        }

        // Fetch full details only for products that fall within the first display page.
        const detailsAlreadyScheduled = detailsScheduledCount;
        const remaining = Math.max(0, DISPLAY_PAGE_SIZE - detailsAlreadyScheduled);
        if (remaining > 0) {
          const idsForDetails = pageProductIds.slice(0, remaining);
          yield* putFindProductsProductDetailsInChunks(
            idsForDetails,
            {
              catalogId,
              storeId,
              localeCode,
              categoryId: categoryId || "",
            },
            { skipFindProductsPagination: true },
          );
          detailsScheduledCount += idsForDetails.length;
        }

        const responseNext = result.payload.next;
        if (responseNext) {
          next = responseNext;
        } else {
          next = undefined;
          break;
        }
      } else {
        next = undefined;
        break;
      }
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: QUERY_PRODUCT_FOR_FIND_PRODUCTS.FAILURE,
      message: e.message,
    });
  }
}

function* handleLoadFindProductsPageDetails(
  action: LoadFindProductsPageDetailsAction,
) {
  try {
    const { page, pageSize, catalogId, storeId, localeCode, categoryId } =
      action.payload;

    const allProductIds: string[] = yield* select(selectGetProductIds);
    const productById: Record<string, any> =
      yield* select(selectProductByIdForFindProducts);

    const pageStart = (page - 1) * pageSize;
    const pageEnd = page * pageSize;
    const pageProductIds = allProductIds.slice(pageStart, pageEnd);

    // Skip products whose full details are already loaded
    const notLoaded = pageProductIds.filter((id) => {
      const p = productById?.[id];
      return !p || !p.productDetailsLoaded;
    });

    if (notLoaded.length > 0) {
      yield* putFindProductsProductDetailsInChunks(notLoaded, {
        catalogId,
        storeId,
        localeCode,
        categoryId,
      });
    }

    const productByIdAfter: Record<string, any> =
      yield* select(selectProductByIdForFindProducts);
    const productsColorData = yield* select(
      (s: AppState) => s.findProducts?.productsColorData ?? {},
    );

    // Color-only path (same page as main grid: details already present, swatches not)
    if (notLoaded.length === 0) {
      const colorOnlyIds = pageProductIds.filter((id) => {
        const p = productByIdAfter?.[id];
        if (!p?.productDetailsLoaded || !p.hasColors) return false;
        return !productsColorData[id]?.productColorDetailsLoaded;
      });
      if (colorOnlyIds.length > 0) {
        yield* put({
          type: FETCH_FIND_PRODUCTS_COLORS.REQUEST,
          payload: {
            productIds: colorOnlyIds,
            storeId: storeId || "",
            localeCode,
            catalogId,
            categoryId: categoryId || "",
            isRefreshingAllProducts: false,
          },
        });
      }
    }

    yield* waitForFindProductsColorsIdle();
  } catch (e: any) {
    console.error(e);
  } finally {
    if (!(yield* cancelled())) {
      yield* put(loadFindProductsPageDetailsComplete());
    }
  }
}

export function* watchLoadFindProductsPageDetails() {
  yield* takeLatest(
    LOAD_FIND_PRODUCTS_PAGE_DETAILS,
    handleLoadFindProductsPageDetails,
  );
}

export function* watchLoadFindProductsTopCategories() {
  yield* takeEvery(
    LOAD_FIND_PRODUCTS_TOP_CATEGORIES,
    loadFindProductsTopCategories,
  );
}

export function* watchCloseAllFindProductsSubscriptions() {
  yield* takeEvery(CLOSE_ALL_FIND_PRODUCT_SUBSCRIPTIONS, closeAllSubscriptions);
}

export function* watchLoadFindProductsChildCategories() {
  yield* takeEvery(
    LOAD_FIND_PRODUCTS_CHILD_CATEGORIES,
    loadFindProductsChildCategories,
  );
}

export function* watchLoadProductsForFindProductsCategory() {
  yield* takeEvery(
    LOAD_PRODUCTS_FOR_FIND_PRODUCTS_CATEGORY,
    loadProductsFromCategory,
  );
}

export function* watchUpdateProductsCategory() {
  yield* takeEvery(CHANGE_PRODUCTS_CATEGORY, changeProductsCategory);
}

export function* watchLoadFindProductModalProductList() {
  yield* takeEvery(
    FETCH_PRODUCTS_FOR_FIND_PRODUCTS.REQUEST,
    loadProductListForFindProductsModal,
  );
}

export function* watchFetchFindProductsColors() {
  yield* takeEvery(
    FETCH_FIND_PRODUCTS_COLORS.REQUEST,
    fetchFindProductsColorsData,
  );
}

export function* watchProductIdsFromFindProductsCategory() {
  yield* takeEvery(
    FETCH_PRODUCT_IDS_FOR_FIND_PRODUCTS_CATEGORY.REQUEST,
    getProductIdsForFindProductsFromCategory,
  );
}

export function* watchQueryProductForFindProducts() {
  yield* takeEvery(
    QUERY_PRODUCT_FOR_FIND_PRODUCTS.REQUEST,
    queryProductForFindProducts,
  );
}

export function* watchCloseTopCategoriesFindProductModal() {
  let task;
  while (true) {
    const action = yield* take(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      loadFindProductsTopCategories,
      action as LoadFindProductsTopCategoriesAction,
    ); // Start a new product loading task
  }
}
export function* watchCloseSubcategoriesFindProductModal() {
  let task;
  while (true) {
    const action = yield* take(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      loadFindProductsChildCategories,
      action as LoadFindProductsChildCategoriesAction,
    ); // Start a new product loading task
  }
}
export function* watchCloseLoadFindProductModal() {
  let task;
  while (true) {
    const action = yield* take(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      loadProductsFromCategory,
      action as LoadProductsFromCategoryForFindProductsCategoryAction,
    ); // Start a new product loading task
  }
}
export function* watchCloseFindProductModal() {
  let task;
  while (true) {
    const action = yield* take(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      loadProductListForFindProductsModal,
      action as loadProductsForFindProductsAction,
    ); // Start a new product loading task
  }
}
